import {
  Button,
  Flex,
  Input,
  LegacyDatePicker,
  LegacyForm,
  Modal,
  Typography,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { BankSaman, Close } from "@payping/icons";
import { useAppTranslation } from "@/utils";
import { SDKDateObject, dateFormats, jalaliNow, toJalali } from "@payping/utils/dateUtils";
import { DepositStatementActionEnum, OrderDirectionEnum, SamanApis } from "@payping/api";
import { closeId, ctaId } from "@/assets/googleTrackIds";
import moment from "jalali-moment";
import React, { SetStateAction } from "react";

interface FormType {
  dateValue: [SDKDateObject | undefined, SDKDateObject | undefined];
  paymentType: DepositStatementActionEnum;
  description?: string;
  order?: OrderDirectionEnum;
}

interface Props {
  openModal: boolean;
  setOpenModal: React.Dispatch<SetStateAction<boolean>>;
}

const Invoice: React.FC<Props> = ({ openModal, setOpenModal }) => {
  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm<FormType>();

  const [{ loading }, onFinish] = useMessagedAsyncFn(async (value: FormType) => {
    const fromDate = toJalali({
      date: value.dateValue[0],
      dateFormat: dateFormats.standard.utcDateTime,
      toFormat: dateFormats.ui.jalaliDate,
    }).object;

    const toDate = toJalali({
      date: value.dateValue[1],
      dateFormat: dateFormats.standard.utcDateTime,
      toFormat: dateFormats.ui.jalaliDate,
    }).object;

    const res = await SamanApis.getDepositStatementsExcelFileWithDepositStatementModelWithCancellationTokenGet(
      value.paymentType,
      fromDate?.toDate(),
      toDate?.toDate(),
      value.order,
    );

    const newWindow = window.open(undefined, "_blank");
    if (res.filePath) {
      newWindow?.location?.assign?.(res.filePath);
    }
  }, []);
  return (
    <Modal
      open={openModal}
      onCancel={() => setOpenModal(false)}
      mask={true}
      closeIcon={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            setOpenModal(false);
          }}
          data-google-track={closeId}
        />
      }
      title={t("digitalAccount.invoice.title")}
      footer={false}
    >
      <Flex vertical gap={32}>
        <Flex vertical gap={16}>
          <div className="tw:flex tw:justify-center">
            <BankSaman width={54} />
          </div>
          <Typography.Title className="tw:flex tw:justify-center" level={4}>
            {t("digitalAccount.invoice.registrationOfSamanInvoiceRequest")}
          </Typography.Title>
          <Typography.Text
            className="tw:flex! tw:justify-center! tw:whitespace-pre-line tw:text-center!"
            type={"secondary"}
          >
            {t("digitalAccount.invoice.desc")}
          </Typography.Text>
        </Flex>
        <LegacyForm onFinish={onFinish} form={form} layout={"vertical"}>
          <Flex vertical gap={16}>
            <LegacyForm.Item name={"dateValue"} rules={[{ required: true }]}>
              <LegacyDatePicker.RangePicker
                presets={[
                  {
                    label: t("common.today"),
                    value: [jalaliNow({ toFormat: dateFormats.standard.utcDateTime }).object, jalaliNow().object],
                  },
                  {
                    label: t("common.thisWeek"),
                    value: [
                      jalaliNow({ manipulate: [{ args: ["week"], method: "startOf" }] }).object,
                      jalaliNow().object,
                    ],
                  },
                  {
                    label: t("common.lastWeek"),
                    value: [
                      jalaliNow({
                        manipulate: [
                          { args: [1, "weeks"], method: "subtract" },
                          { args: ["week"], method: "startOf" },
                        ],
                      }).object,
                      jalaliNow({
                        manipulate: [
                          { args: [1, "weeks"], method: "subtract" },
                          { args: ["week"], method: "endOf" },
                        ],
                      }).object,
                    ],
                  },
                  {
                    label: t("common.thisMonth"),
                    value: [
                      jalaliNow({ manipulate: [{ args: ["month"], method: "startOf" }] }).object,
                      jalaliNow().object,
                    ],
                  },
                  {
                    label: t("common.lastMonth"),
                    value: [
                      jalaliNow({
                        manipulate: [{ args: [30, "days"], method: "subtract" }],
                      }).object,
                      jalaliNow().object,
                    ],
                  },
                  {
                    label: t("common.thisYear"),
                    value: [
                      jalaliNow({ manipulate: [{ args: ["year"], method: "startOf" }] }).object,
                      jalaliNow().object,
                    ],
                  },
                  {
                    label: t("common.lastYear"),
                    value: [
                      jalaliNow({
                        manipulate: [{ args: [366, "days"], method: "subtract" }],
                      }).object,
                      jalaliNow().object,
                    ],
                  },
                ]}
                disabledDate={(date) =>
                  !date || date.isBefore(moment().subtract(2, "years"), "day") || date.isAfter(moment(), "day")
                }
                className="tw:w-full"
                allowClear
              />
            </LegacyForm.Item>
            <LegacyForm.Item label={t("description")} name={"description"}>
              <Input placeholder={t("digitalAccount.invoice.textDescription")} />
            </LegacyForm.Item>
            <LegacyForm.Item noStyle>
              <div className="tw:flex tw:justify-end">
                <Flex gap={"small"}>
                  <Button onClick={() => setOpenModal(false)} type={"default"}>
                    {t("cancel")}
                  </Button>
                  <Button
                    loading={loading}
                    className="tw:w-fit"
                    type={"primary"}
                    htmlType={"submit"}
                    data-google-track={ctaId}
                  >
                    {t("digitalAccount.invoice.getExcel")}
                  </Button>
                </Flex>
              </div>
            </LegacyForm.Item>
          </Flex>
        </LegacyForm>
      </Flex>
    </Modal>
  );
};

export default Invoice;
